import { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const comic = comics.find((c) => c.id === id);
  const category = comic ? categories.find((cat) => cat.slug === comic.category) : null;
  const relatedComics = comic
    ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <p className="text-foreground-500">漫画不存在</p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <a href="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</a>
              <span>/</span>
              {category && (
                <>
                  <a href={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                    {category.name}韩漫
                  </a>
                  <span>/</span>
                </>
              )}
              <span className="text-foreground-900 font-medium">{comic.title}</span>
            </div>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[280px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 布丁漫画韩漫推荐`}
                    title={`${comic.title} - 布丁漫画韩漫推荐`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {comic.isHot && (
                    <span className="px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      热门推荐
                    </span>
                  )}
                  {comic.isNew && (
                    <span className="px-2.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      新番上架
                    </span>
                  )}
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{comic.title}</h1>
                <p className="text-sm text-foreground-500 mb-4">{comic.author}</p>

                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-lg font-bold text-accent-600">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/10</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">{comic.chapters}话</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">更新于 {comic.updateDate}</span>
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-6">
                  <h3 className="text-sm font-medium text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                    开始阅读
                  </button>
                  <button className="px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                    收藏
                  </button>
                  <button className="px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-share-forward-line"></i>
                    </div>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-heading text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}